import {
	type AddTwoFAMethodResponse,
	type AuthRepository,
	type LoginCredentials,
	type LoginResponse,
	PlatformOptions,
	type SignupRequest,
	type TokenPair,
	type TwoFactorMethod,
	type TwoFactorMethodType
} from '@contexts/auth/domain';
import {
	type DeleteResponse,
	type EnvironmentRepository,
	type GetResponse,
	hasValue,
	type HttpService,
	type PostResponse,
	type PutResponse
} from '@contexts/shared/domain';

export function restApiAuthRepositoryBuilder(
	environment: EnvironmentRepository,
	httpService: HttpService
): AuthRepository {
	return {
		async login({ email, password }: LoginCredentials): Promise<LoginResponse> {
			const response = await httpService.post<LoginResponse>(
				`${environment.apiBaseUrl}/auth/login`,
				{
					email,
					password
				},
				undefined
			);
			return response.data;
		},
		async logout(): Promise<void> {
			await httpService.post<{ token: string }>(
				`${environment.apiBaseUrl}/auth/logout`,
				undefined,
				undefined
			);
		},
		async signup(signupRequest: SignupRequest): Promise<string> {
			const response = await httpService.post<PostResponse<string>>(
				`${environment.apiBaseUrl}/auth/signup`,
				signupRequest,
				undefined
			);
			return response.data.item;
		},
		async twoFactorLogin(
			token: string,
			code: string,
			methodType: TwoFactorMethodType,
			rememberMe: boolean
		): Promise<TokenPair> {
			const response = await httpService.post<TokenPair>(
				`${environment.apiBaseUrl}/auth/twofa/login`,
				{
					token,
					code,
					methodType,
					platform: PlatformOptions.WEB,
					rememberMe
				},
				undefined
			);

			return response.data;
		},
		async refresh(refreshToken?: string): Promise<TokenPair> {
			const response = await httpService.post<TokenPair>(
				`${environment.apiBaseUrl}/auth/refresh`,
				hasValue(refreshToken) ? { refreshToken } : {},
				undefined
			);

			return response.data;
		},
		async activateUser(token: string, password: string): Promise<{ token: string }> {
			const response = await httpService.post<PostResponse<{ token: string }>>(
				`${environment.apiBaseUrl}/users/activate`,
				{
					token,
					password
				},
				undefined
			);

			return response.data.item;
		},
		async revertUserActivation(token: string): Promise<void> {
			await httpService.post<PostResponse<{ token: string }>>(
				`${environment.apiBaseUrl}/users/revert-activation`,
				{
					token
				},
				undefined
			);
		},
		async addTwoFactorMethod(methodType: TwoFactorMethodType): Promise<AddTwoFAMethodResponse> {
			const response = await httpService.post<string>(
				`${environment.apiBaseUrl}/auth/2fa/methods`,
				{
					methodType
				},
				undefined
			);

			return { token: response.data };
		},
		async update2FAMethod(
			methodId: string,
			status?: string,
			isDefault?: boolean
		): Promise<TwoFactorMethod> {
			const response = await httpService.put<PutResponse<TwoFactorMethod>>(
				`${environment.apiBaseUrl}/auth/2fa/methods/${methodId}`,
				{
					status,
					isDefault
				},
				undefined
			);
			return response.data.item;
		},
		async delete2FAMethod(methodId: string): Promise<string> {
			const response = await httpService.delete<DeleteResponse>(
				`${environment.apiBaseUrl}/auth/2fa/methods/${methodId}`
			);

			return response.data.id;
		},
		async getUserMethods(): Promise<TwoFactorMethod[]> {
			const response = await httpService.get<TwoFactorMethod[]>(
				`${environment.apiBaseUrl}/auth/2fa/methods`,
				undefined,
				undefined
			);

			return response.data;
		},
		async changePassword(password: string): Promise<void> {
			await httpService.put<{ token: string }>(
				`${environment.apiBaseUrl}/auth/change-password`,
				{
					password
				},
				undefined
			);
		},
		async recoveryPassword(newPassword: string, token: string): Promise<void> {
			await httpService.put<{ token: string }>(
				`${environment.apiBaseUrl}/auth/recovery-password`,
				{
					newPassword,
					token
				},
				undefined
			);
		},
		async resetPassword(email: string): Promise<void> {
			await httpService.put(
				`${environment.apiBaseUrl}/auth/reset-password`,
				{
					email
				},
				undefined
			);
		},
		async checkUserStatus(token: string): Promise<string> {
			const response = await httpService.get<GetResponse<string>>(
				`${environment.apiBaseUrl}/users/status?token=${token}`,
				undefined,
				undefined
			);

			return response.data.item;
		},
		async unlockUser(id: string): Promise<void> {
			await httpService.post<GetResponse<string>>(
				`${environment.apiBaseUrl}/users/${id}/unlock`,
				undefined,
				undefined
			);
		},
		async suplantUser(userId: string): Promise<TokenPair> {
			const response = await httpService.post<TokenPair>(
				`${environment.apiBaseUrl}/auth/suplant/user/${userId}`,
				{
					platform: PlatformOptions.WEB
				},
				undefined
			);

			return response.data;
		},
		async suplantUserByDeviceIdentificator(deviceIdentificator: string): Promise<TokenPair> {
			const response = await httpService.post<TokenPair>(
				`${environment.apiBaseUrl}/auth/suplant/device/identificator/${deviceIdentificator}`,
				{
					platform: PlatformOptions.WEB
				},
				undefined
			);

			return response.data;
		},
		async revertSuplant(): Promise<void> {
			await httpService.post<void>(
				`${environment.apiBaseUrl}/auth/suplant/revert`,
				undefined,
				undefined
			);
		},
		async generateApiKey(): Promise<string> {
			const response = await httpService.post<string>(
				`${environment.apiBaseUrl}/auth/api-key`,
				undefined,
				undefined
			);

			return response.data;
		}
	};
}
